/**
 * RPT-07 — a compact delivery performance summary card on Home.
 *
 * The deliverable figures are the contract model's (D113): one ListOccurrences
 * per program the portfolio dashboard lists, summed by the same fold the
 * Command Center uses (Σ onTime / Σ timingKnown), so the two pages cannot
 * disagree. The open corrective-action count is GetOpenCorrectiveActionCounts.
 *
 * companyId is a prop, not a useTenantId() call: an unauthenticated viewer
 * must see the card's own loading/unavailable state, not a thrown error.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraEmptyState, SpecraInlineBanner, SpecraLink, SpecraMetricStrip, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { useOpenCorrectiveActions } from '../../components/delivery/widgets/useOpenCorrectiveActions';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { countsOnTimePercent, useDeliverableCounts, usePortfolioDashboard } from '../Delivery/CommandCenter/hooks';
import { OccurrenceWindow } from '../Delivery/Deliverables/hooks';
import { addDays, cmDate, todayISO } from '../Delivery/Deliverables/lib/format';
/** A figure's own pending/failed/empty states: never a bare dash or a fake zero. */ function unavailableContent(fold) {
    if (fold.isError) return 'Unavailable';
    if (fold.isLoading) return 'Loading…';
    return null;
}
/** The on-time figure, and whether it reads as a quiet absence rather than a number. */ function onTimeFigure(fold) {
    const pending = unavailableContent(fold);
    if (pending) return {
        value: pending,
        muted: true
    };
    const pct = countsOnTimePercent(fold.total);
    if (pct === null) return {
        value: 'No approvals yet',
        muted: true
    };
    return {
        value: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                fold.total.onTime,
                " of ",
                fold.total.timingKnown,
                ' ',
                /*#__PURE__*/ _jsxs(SpecraText, {
                    tone: "muted",
                    children: [
                        "(",
                        pct,
                        "%)"
                    ]
                })
            ]
        }),
        muted: false
    };
}
// "Late" is an honest total the customer PM asked for: delivered late
// (approved, but after the due date) PLUS overdue (past due, not yet
// approved) — two failure modes folded into one number, with a sublabel
// splitting them back out so neither hides inside the other.
function lateSplit(c) {
    const deliveredLate = c.timingKnown - c.onTime;
    const overdue = c.stateCounts.late ?? 0;
    return {
        deliveredLate,
        overdue
    };
}
const LOADING_METRICS = [
    'On-time (cumulative)',
    'On-time (last 7 days)',
    'Late',
    'Open corrective actions'
].map((label)=>({
        id: label,
        label,
        value: null
    }));
export function DeliverySummaryCard({ companyId }) {
    const ready = !!companyId;
    const dashboard = usePortfolioDashboard({
        companyId: companyId ?? ''
    });
    const openCorrectiveActions = useOpenCorrectiveActions({
        companyId: companyId ?? '',
        enabled: ready
    });
    // The deliverable figures are the contract model's (D113): one
    // ListOccurrences per visible program, folded here. Cumulative is every
    // date; "last 7 days" narrows to dates whose latest filing is in the week
    // ending today.
    const programIds = useMemo(()=>(dashboard.data?.programs ?? []).map((p)=>p.programId), [
        dashboard.data
    ]);
    const today = todayISO();
    const weekFrom = addDays(today, -6);
    const cumulative = useDeliverableCounts(programIds, {
        window: OccurrenceWindow.ALL,
        pageSize: 1
    });
    const lastWeek = useDeliverableCounts(programIds, {
        window: OccurrenceWindow.ALL,
        pageSize: 1,
        submittedFrom: weekFrom,
        submittedTo: today
    });
    // The dashboard settling at zero programs is a definite answer (the viewer
    // has no program access), not a loading/error state — it gets its own
    // empty idiom instead of the four figures reading as zeros.
    const noPrograms = ready && dashboard.isSuccess && dashboard.data.totalPrograms === 0;
    // Not-ready (no companyId yet, e.g. auth still resolving) reads the same as
    // loading: never render the figures grid before we know whether there's
    // program access to report on.
    const dashboardLoading = !ready || dashboard.isLoading;
    const dashboardError = ready && dashboard.isError;
    const late = lateSplit(cumulative.total);
    const lateUnavailable = unavailableContent(cumulative);
    const partial = Math.max(cumulative.failed, lastWeek.failed);
    const openCAUnavailable = !ready || openCorrectiveActions.isLoading || openCorrectiveActions.isError;
    const openCACount = Number(openCorrectiveActions.data?.totalOpenCount ?? 0);
    // The deliverable figures sum many programs, so there is no one list behind
    // them to open here: the tiles are read-only, and the Command Center link
    // above goes per program.
    const cumulativeFigure = onTimeFigure(cumulative);
    const weekFigure = onTimeFigure(lastWeek);
    const metrics = [
        {
            id: 'cumulative',
            testid: Testids.deliverySummaryCard.cumulativeFigure,
            label: 'On-time (cumulative)',
            ...cumulativeFigure,
            title: 'Deliverables approved on time over those approved with a known date'
        },
        {
            id: 'previous-week',
            testid: Testids.deliverySummaryCard.previousWeekFigure,
            label: 'On-time (last 7 days)',
            ...weekFigure,
            note: `Filed ${cmDate(weekFrom)} – ${cmDate(today)}`,
            title: 'Of the deliverables filed in the last 7 days, those approved on time'
        },
        {
            id: 'late',
            testid: Testids.deliverySummaryCard.lateFigure,
            label: 'Late',
            value: lateUnavailable ?? late.deliveredLate + late.overdue,
            muted: !!lateUnavailable,
            note: lateUnavailable ? undefined : `${late.deliveredLate} delivered late / ${late.overdue} overdue`,
            title: 'Approved after the due date, plus past due and not yet approved'
        },
        {
            id: 'open-corrective-actions',
            testid: Testids.deliverySummaryCard.openCorrectiveActionsFigure,
            label: 'Open corrective actions',
            value: openCAUnavailable ? '—' : openCACount,
            muted: openCAUnavailable
        }
    ];
    const byId = new Map(metrics.map((m)=>[
            m.id,
            m
        ]));
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-6",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                action: /*#__PURE__*/ _jsx(SpecraLink, {
                    className: "text-[length:var(--fs-ui-caption)]",
                    href: "/delivery/command-center",
                    renderLink: renderRouterLink,
                    children: "Command Center →"
                }),
                title: "Delivery Performance"
            }),
            /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.deliverySummaryCard.root,
                children: dashboardLoading ? /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                    "data-testid": Testids.deliverySummaryCard.loading,
                    loading: true,
                    metrics: LOADING_METRICS
                }) : dashboardError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    "data-testid": Testids.deliverySummaryCard.error,
                    tone: "danger",
                    variant: "framed",
                    children: "The delivery performance summary could not be loaded."
                }) : noPrograms ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    "data-testid": Testids.deliverySummaryCard.empty,
                    framed: true,
                    size: "compact",
                    title: "No delivery programs are visible to this account."
                }) : /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                    getItemProps: (m)=>({
                            title: byId.get(m.id)?.title
                        }),
                    getItemTestId: (m)=>byId.get(m.id)?.testid,
                    metrics: metrics
                })
            }),
            partial > 0 && !dashboardLoading && !dashboardError && !noPrograms && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "mt-1.5",
                "data-testid": Testids.cmPortfolio.partialNote,
                size: "caption",
                tone: "danger",
                children: [
                    "Deliverable figures could not be read for ",
                    partial,
                    ' ',
                    partial === 1 ? 'program' : 'programs',
                    "; these totals leave",
                    ' ',
                    partial === 1 ? 'it' : 'them',
                    " out."
                ]
            })
        ]
    });
}
